import {
  Flex,
  Text,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Grid,
  Box,
  Input,
  Spinner,
} from "@chakra-ui/react";
import CheckIcon from "../icons/CheckIcon";
import { Command, CommandStatus } from "../../types/command.types";
import { useCallback, useMemo, useRef, useState } from "react";
import ClientSideTable from "../table/ClientSideTable";
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import OrderStatusBadge from "../OrderStatusBadge";
import TrashIcon from "../icons/TrashIcon";
import { useMutation, useQueryClient } from "react-query";
import { bulkChangeCommandStatus } from "../../mutations/command.mutations";
import { useSimpleToast } from "../../hooks/toast.hooks";
import FileInput from "../FileInput";
import { fetchCommandByExposedId } from "../../queries/command.queries";
import SelectAdmin from "../inputs/SelectAdmin";
import { User } from "../../types/user.types";
import { printLabels } from "../../hooks/printers.hooks";

type ChangeStatusProps = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  goBack: () => void;
  status: CommandStatus;
  setStatus: (status: CommandStatus) => void;
};

function ChangeStatusModal({
  isOpen,
  status,
  setStatus,
  onClose,
  goBack,
}: ChangeStatusProps) {
  const [step, setStep] = useState<number>(1);
  const [grillNumber, setGrillNumber] = useState<string>("");
  const [printingFiles, setPrintingFiles] = useState<File[] | null>(null);
  const handleStatusChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setStep(1);
      setGrillNumber("");
      setPrintingFiles(null);
      setStatus(
        CommandStatus[event.target.value as keyof typeof CommandStatus]
      );
    },
    [setStatus, setStep]
  );
  const [scannedCommands, setScannedCommands] = useState<Command[]>([]);

  const toast = useSimpleToast();
  const queryClient = useQueryClient();

  const { mutate: bulkChangeStatus, isLoading: bulkChangeStatusIsLoading } = useMutation(bulkChangeCommandStatus, {
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      if (status === CommandStatus.READY_FOR_DELIVERY) {
        await printLabels(scannedCommands.map((sc) => sc.id), "Imprimante Etiquettes")
      }
      toast.success("Commandes", "Les statuts des commandes ont été mis à jour");
      onClose();
    },
  });
  const handleConfirm = useCallback(() => {
    if (step === 1 && status === CommandStatus.PRINTING) {
      setStep(2);
    } else {
      // TODO: Handle change specific to in printing
      bulkChangeStatus({
        ids: scannedCommands.map((sc) => sc.id),
        status: status,
        managerId: manager?.id,
      });
    }
  }, [bulkChangeStatus, status, step, setStep, scannedCommands, printingFiles]);

  const handleRemoveItem = useCallback(
    (command: Command) => {
      setScannedCommands(scannedCommands.filter((sc) => sc.id !== command.id));
    },
    [scannedCommands]
  );

  const handleGoBack = useCallback(() => {
    setStep(1);
    goBack();
  }, [setStep, goBack]);

  const commandCode = useRef("");
  const handleScannerInputPaste = useCallback(
    async (event: React.ClipboardEvent) => {
      try {
        // 1. Parse code from clipboard
        const text = event.clipboardData.getData("text");
        // 2. Ensure not already in list
        if (
          scannedCommands.findIndex(
            (command) => command.exposedId.toString() === text
          ) !== -1
        ) {
          toast.info("Scan", "Le code est déjà présent dans la liste.");
          return;
        }
        // 3. Try to retrieve from backend
        const command = await fetchCommandByExposedId(text);
        if (command !== null) {
          setScannedCommands([command, ...scannedCommands]);
        }
      } catch (err) {
        toast.error("Scan", "Le code n'a pas pu être lu.");
      }
    },
    [scannedCommands, toast]
  );
  const handleScannerOnKeyDown = async (event: React.KeyboardEvent) => {
    switch (event.code) {
      case "Enter": {
        const code = commandCode.current.toLowerCase().split("§").join("-");
        commandCode.current = "";
        const command = await fetchCommandByExposedId(code);
        if (command !== null) {
          setScannedCommands([command, ...scannedCommands]);
        }
        commandCode.current = "";
        break;
      }
      case "Shift":
      case "Meta":
      case "ShiftLeft":
        break;
      default: {
        commandCode.current = commandCode.current + event.key;
        break;
      }
    }
  };

  const columns = useMemo<ColumnDef<Command>[]>(
    () => [
      {
        accessorFn: (row) => row.exposedId,
        id: "exposed_id",
        header: "ID",
        cell: (info) => info.getValue(),
      },
      {
        accessorFn: (row) => {
          if (row.patient) {
            return `${row.patient.firstName} ${row.patient.lastName}`;
          }
          return `---`;
        },
        id: "patient",
        header: "Patient",
        cell: (info) => info.getValue(),
      },
      {
        accessorFn: (row) => format(row.createdAt, "dd/MM/yyyy"),
        id: "created_at",
        header: "Date de création",
        cell: (info) => info.getValue(),
      },
      {
        accessorFn: (row) => (
          <OrderStatusBadge
            halted={row.haltedAt != null}
            status={row.status}
          ></OrderStatusBadge>
        ),
        id: "status",
        header: "Statut",
        cell: (info) => info.getValue(),
      },
      {
        accessorFn: (row) => (
          <Flex justifyContent={"end"}>
            <TrashIcon
              color={"gray.500"}
              onClick={() => handleRemoveItem(row)}
            ></TrashIcon>
          </Flex>
        ),
        id: "more",
        header: "",
        cell: (info) => info.getValue(),
      },
    ],
    [handleRemoveItem]
  );
  const table = useReactTable({
    data: scannedCommands ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });
  const [manager, setManager] = useState<User | undefined>(undefined);
  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered>
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6.9px)" />
      <ModalContent
        maxW={"none"}
        w="min(1800px, 80vw)"
        onPaste={handleScannerInputPaste}
        onKeyDown={handleScannerOnKeyDown}
      >
        <ModalHeader>
          <Flex alignItems={"center"}>
            <Flex
              w="48px"
              h="48px"
              bg="zinc.50"
              border="solid 1px"
              borderColor="brand.100"
              justifyContent="center"
              alignItems="center"
              borderRadius={100}
              mr="8px"
            >
              <CheckIcon w="24px" h="24px" color="brand.700"></CheckIcon>
            </Flex>
            <Text lineHeight={"48px"}>Modifier le statut à</Text>
            <Select
              value={status}
              onChange={handleStatusChange}
              border={0}
              w="fit-content"
              backgroundColor={"brand.600"}
              color={"white"}
              h="1.5em"
              pl={4}
            >
              {/* <option value={CommandStatus.DRAFT}>Brouillon</option> */}
              <option value={CommandStatus.WAITING_FOR_RECEPTION}>
                Attente de réception
              </option>
              <option value={CommandStatus.RECEIVED}>Réceptionné</option>
              <option value={CommandStatus.MODELING}>En modélisation</option>
              <option value={CommandStatus.PRINTING}>En impression</option>
              <option value={CommandStatus.POLISHING}>En finition</option>
              <option value={CommandStatus.READY_FOR_DELIVERY}>
                Prêt à livrer
              </option>
              {/* <option value={CommandStatus.IN_DELIVERY}>Expédié</option> */}
              {/* <option value={CommandStatus.DELIVERED}>Terminé</option> */}
              <option value={CommandStatus.ABORTED}>Annulé</option>
              <option value={CommandStatus.PENDING}>En pause</option>
            </Select>
          </Flex>
        </ModalHeader>
        <ModalCloseButton onClick={onClose} />
        <ModalBody>
          {status === CommandStatus.PRINTING && step === 1 && (
            <Flex
              justifyContent="center"
              alignItems="center"
              direction="column"
            >
              <Input
                w="400px"
                placeholder="Numéro de la grille d'impression"
                name={"grille_name"}
                value={grillNumber}
                onChange={(event) => setGrillNumber(event.target.value)}
              ></Input>
              <Box py={4} w="100%">
                <FileInput
                  name={""}
                  maxNumberOfFiles={1}
                  onFileChange={(files: File[] | null) =>
                    setPrintingFiles(files)
                  }
                ></FileInput>
              </Box>
            </Flex>
          )}
          {(status !== CommandStatus.PRINTING || step === 2) && (
            <>
              <SelectAdmin
                placeholder="Ajouter un responsable"
                user={manager}
                setUser={setManager}
              ></SelectAdmin>
              <ClientSideTable
                table={table}
                w={["auto", "240px", "200px", "300px", "10px"]}
                flexGrow={[1, 0, 0, 0, 0]}
              ></ClientSideTable>
            </>
          )}
        </ModalBody>
        <ModalFooter borderTop="solid 1px #E2E8F0">
          <Grid gridTemplateColumns={"1fr 1fr"} gap="40px" w="100%">
            <Flex p={3} justifyContent="center" onClick={handleGoBack}>
              Retour
            </Flex>
            { bulkChangeStatusIsLoading ? <Flex
              p={3}
              justifyContent="center"
              bg="brand.300"
              color="white"
              borderRadius={"xl"}
              onClick={handleConfirm}
            >
              <Spinner pr={4} />
              Confirmer
            </Flex> : <Flex
              p={3}
              justifyContent="center"
              bg="brand.700"
              color="white"
              borderRadius={"xl"}
              onClick={handleConfirm}
            >
              Confirmer
            </Flex>
            }
          </Grid>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ChangeStatusModal;
